import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client, { API } from "@/api/client";
import Layout from "@/components/Layout";
import WelcomeModal from "@/components/WelcomeModal";
import { useAuth } from "@/context/AuthContext";
import { Lock, Check, Bookmark, ChevronRight, Megaphone, CalendarDays, Image as ImageIcon } from "lucide-react";
export default function Dashboard() {
  const { user } = useAuth();
  const [levels, setLevels] = useState([]);
  const [anns, setAnns] = useState([]);
  const [outlook, setOutlook] = useState(null);
  const [chartIndex, setChartIndex] = useState([]);
  const [chartIndexOpen, setChartIndexOpen] = useState(false);
  const [openLevels, setOpenLevels] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, a, o, ci] = await Promise.all([
          client.get("/curriculum"),
          client.get("/announcements"),
          client.get("/outlooks/latest"),
          client.get("/charts/index"),
        ]);
        setLevels(c.data.levels);
        setAnns(a.data.announcements);
        setOutlook(o.data.outlook);
        setChartIndex(ci.data.entries);
        // On mobile default only first level open; desktop CSS ignores this state.
        const first = c.data.levels?.[0]?.level;
        if (first) setOpenLevels({ [first]: true });
      } finally { setLoading(false); }
    })();
  }, []);

  const toggleLevel = (lvl) => setOpenLevels((s) => ({ ...s, [lvl]: !s[lvl] }));

  const totalModules = levels.reduce((n, l) => n + l.modules.length, 0);
  const doneModules = levels.reduce((n, l) => n + l.modules.filter(m => m.is_complete).length, 0);
  const pct = totalModules ? Math.round(100 * doneModules / totalModules) : 0;

  return (
    <Layout>
      <WelcomeModal />
      <section className="mb-14" data-testid="dashboard-hero">
        <p className="overline">Welcome back</p>
        <h1 className="font-display text-5xl md:text-6xl leading-none mt-2">{user?.name}.</h1>
        <div className="mt-8 grid md:grid-cols-3 gap-6 items-end">
          <div className="md:col-span-2">
            <div className="flex justify-between items-baseline mb-3">
              <span className="overline">Curriculum Progress</span>
              <span className="font-display text-3xl text-[#D4AF37]" data-testid="progress-pct">{pct}%</span>
            </div>
            <div className="progress-bar-track"><div className="progress-bar-fill" style={{ width: `${pct}%` }} /></div>
            <p className="text-sm text-neutral-500 mt-3 font-body">
              {doneModules} of {totalModules} modules complete · {8} levels · Forex + Futures
            </p>
          </div>
          <div className="border border-[#1f1f1f] p-5">
            <p className="overline mb-2">Certificate</p>
            <p className="text-sm text-neutral-400 mb-4">Awarded on completion of every module &amp; quiz.</p>
            <Link to="/certificate" className="btn-outline-gold inline-block" data-testid="cta-cert">View Certificate</Link>
          </div>
        </div>
      </section>

      {outlook && (
        <section className="mb-14" data-testid="outlook-card">
          <div className="flex items-center gap-2 mb-4">
            <CalendarDays size={16} className="text-[#D4AF37]" />
            <p className="overline">This Week&apos;s Boardroom Outlook</p>
          </div>
          <Link to="/outlook" className="block border border-[#D4AF37]/40 bg-[#0d0d0d] p-6 hover:border-[#D4AF37] transition">
            <p className="text-[10px] uppercase tracking-widest text-[#AA8A25]">Week of {outlook.week_of}</p>
            <p className="font-display text-3xl mt-1 mb-3 text-white">{outlook.title}</p>
            {outlook.bias && (
              <p className="text-sm text-neutral-400 line-clamp-2 font-body">{outlook.bias}</p>
            )}
            <p className="mt-4 text-[10px] uppercase tracking-widest text-[#D4AF37]">
              Read the full outlook <ChevronRight size={12} className="inline" />
            </p>
          </Link>
        </section>
      )}

      {chartIndex.length > 0 && (
        <section className="mb-14" data-testid="chart-index-section">
          <button
            onClick={() => setChartIndexOpen((v) => !v)}
            data-testid="chart-index-toggle"
            className="w-full flex items-center justify-between border border-[#1f1f1f] bg-[#0a0a0a] hover:border-[#D4AF37]/40 transition p-5 text-left">
            <div className="flex items-center gap-3">
              <ImageIcon size={16} className="text-[#D4AF37]" />
              <div>
                <p className="overline">Chart Index</p>
                <p className="font-display text-2xl mt-1">
                  {chartIndex.length} annotated charts across the curriculum
                </p>
              </div>
            </div>
            <ChevronRight size={16} className={`text-neutral-500 transition ${chartIndexOpen ? "rotate-90 text-[#D4AF37]" : ""}`} />
          </button>

          {chartIndexOpen && (
            <div className="mt-4 grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="chart-index-list">
              {chartIndex.map((e) => (
                <Link key={e.image_id}
                      to={`/module/${e.module_id}`}
                      data-testid={`chart-index-entry-${e.image_id}`}
                      className="card-boardroom p-3 flex gap-3 items-start group">
                  <img
                    src={`${API}/files/${e.image_id}?auth=${localStorage.getItem("boardroom_token") || ""}`}
                    alt={e.caption}
                    className="w-24 h-16 object-cover flex-shrink-0 border border-[#1f1f1f]"
                    draggable={false} />
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-[9px] text-neutral-500 tracking-widest">
                      L{e.level} · {e.module_title}
                    </p>
                    <p className="font-display text-base text-white leading-tight mt-1 group-hover:text-[#D4AF37] transition">
                      {e.lesson_title}
                    </p>
                    <p className="text-[11px] text-neutral-500 mt-1 font-body line-clamp-2">
                      {e.caption}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {anns.length > 0 && (
        <section className="mb-14" data-testid="announcements-section">
          <div className="flex items-center gap-2 mb-4">
            <Megaphone size={16} className="text-[#D4AF37]" />
            <p className="overline">From the Boardroom</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {anns.slice(0, 2).map((a) => (
              <div key={a.id} className="border border-[#D4AF37]/25 bg-[#0d0d0d] p-5">
                <p className="font-display text-2xl text-[#D4AF37]">{a.title}</p>
                <p className="text-sm text-neutral-300 mt-2 font-body whitespace-pre-line">{a.body}</p>
                <p className="text-[10px] text-neutral-600 mt-3 tracking-widest uppercase">
                  {new Date(a.at).toLocaleDateString()} · {a.author}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {loading && <p className="text-neutral-500">Loading curriculum…</p>}

      {levels.map((level) => {
        const isOpen = openLevels[level.level];
        const doneInLevel = level.modules.filter(m => m.is_complete).length;
        return (
        <section key={level.level} className="mb-10 md:mb-16" data-testid={`level-${level.level}`}>
          <button
            type="button"
            onClick={() => toggleLevel(level.level)}
            data-testid={`level-toggle-${level.level}`}
            className="w-full md:w-auto md:pointer-events-none flex items-center justify-between text-left md:block group">
            <div className="flex items-baseline gap-3 md:gap-4 mb-4 md:mb-6">
              <span className="font-display text-5xl md:text-6xl text-[#D4AF37]/50">0{level.level}</span>
              <div>
                <p className="overline">Level {level.level} · {doneInLevel}/{level.modules.length} complete</p>
                <h2 className="font-display text-2xl md:text-3xl">{level.title}</h2>
              </div>
            </div>
            <ChevronRight
              size={18}
              className={`md:hidden text-[#D4AF37] transition-transform ${isOpen ? "rotate-90" : ""}`}
              aria-hidden />
          </button>
          <div className="gold-divider mb-6 md:mb-8" />
          <div className={`${isOpen ? "grid" : "hidden"} md:grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5`}>
            {level.modules.map((m, i) => {
              const inner = (
                <>
                <div className="flex justify-between items-start mb-4">
                  <span className="font-mono text-[10px] text-neutral-500 tracking-widest">
                    L{level.level}·{String(i + 1).padStart(2, "0")}
                  </span>
                  {m.locked ? (
                    <span className="flex items-center gap-1 text-neutral-500 text-[10px] uppercase tracking-widest" data-testid={`module-locked-${m.id}`}>
                      <Lock size={12} /> Locked
                    </span>
                  ) : m.is_complete ? (
                    <span className="flex items-center gap-1 text-[#D4AF37] text-[10px] uppercase tracking-widest">
                      <Check size={12} /> Complete
                    </span>
                  ) : m.progress_pct > 0 ? (
                    <span className="text-[10px] text-neutral-500 uppercase tracking-widest">{m.progress_pct}%</span>
                  ) : null}
                </div>
                <h3 className={`font-display text-2xl mb-3 transition ${m.locked ? "text-neutral-500" : "group-hover:text-[#D4AF37]"}`}>{m.title}</h3>
                <p className="text-sm text-neutral-500 mb-6 font-body leading-relaxed">{m.summary}</p>
                <div className="progress-bar-track mb-4"><div className="progress-bar-fill" style={{ width: `${m.progress_pct || 0}%` }} /></div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-neutral-500 uppercase tracking-widest">
                    {m.completed_count}/{m.lesson_count} Lessons
                  </span>
                  {m.locked ? (
                    <Lock size={16} className="text-neutral-600" />
                  ) : (
                    <ChevronRight size={16} className="text-neutral-600 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition" />
                  )}
                </div>
                </>
              );
              return m.locked ? (
                <div key={m.id} data-testid={`module-card-${m.id}`}
                     className="card-boardroom p-6 block group relative opacity-60 cursor-not-allowed"
                     title="Complete the previous module first">
                  {inner}
                </div>
              ) : (
                <Link key={m.id} to={`/module/${m.id}`} data-testid={`module-card-${m.id}`}
                      className="card-boardroom p-6 block group relative">
                  {inner}
                </Link>
              );
            })}
          </div>
        </section>
      );
      })}
    </Layout>
  );
}
